<!-- eslint-disable max-len -->
<template>
  <div class="home-empty-state">
    <img
      v-if="sidebarFilter === 'MY_STUDIO'"
      class="home-empty-state__illustration-artistic"
      src="@/assets/disco-artistic.png"
      alt=""
    >
    <img
      v-else-if="sidebarFilter === 'TRASH'"
      class="home-empty-state__illustration-keiki"
      src="@/assets/keiki-telling-joke.png"
      alt=""
    >
    <img
      v-else
      class="home-empty-state__illustration-sniffing"
      src="@/assets/disco-sniffing.png"
      alt=""
    >
    <div class="home-empty-state__body">
      <!-- Teachers with no drafts -->
      <template v-if="sidebarFilter === 'MY_STUDIO' && !userIsStudent">
        <!-- Teachers should use an Activity Template if possible -->
        <template v-if="userHasActivityTemplates">
          <h2>{{ $t('Welcome to Studio!') }}</h2>
          <p>
            {{ $t('Studio is a digital maker space from Discovery Education to make lessons and activities.') }}
          </p>
          <p>
            {{ $t('Get started by using Activity Templates.') }}
          </p>
          <RouterLink
            class="home-empty-state__button"
            :to="{ name: 'activity-templates' }"
          >
            {{ $t('Browse Activity Templates') }}
          </RouterLink>
        </template>
        <!-- Teachers without Activity Templates should view the Teacher Scavenger Hunt -->
        <template v-else>
          <h2>{{ $t('Welcome to Studio!') }}</h2>
          <p>
            {{ $t('Studio is a digital maker space from Discovery Education to make lessons and activities.') }}
          </p>
          <p>
            {{ $t('Complete our interactive Studio Scavenger Hunt to learn how Studio can save you time and engage your students!') }}
          </p>
          <RouterLink
            class="home-empty-state__button"
            to="view?id=74853e26-5b28-4b79-9f13-576345c6df66"
          >
            {{ $t('Launch Scavenger Hunt') }}
          </RouterLink>
        </template>
      </template>

      <!-- Students with no drafts should view the Student Scavenger Hunt -->
      <template v-else-if="sidebarFilter === 'MY_STUDIO' && userIsStudent">
        <h2>{{ $t('Welcome to Studio!') }}</h2>
        <p>
          {{ $t('You don’t have anything created...yet! Learn how to use Studio with our Student Scavenger Hunt.') }}
        </p>
        <RouterLink
          class="home-empty-state__button"
          to="view?id=f599f802-f8da-4068-ba00-4512cbe11d89"
        >
          {{ $t('Go to Scavenger Hunt') }}
        </RouterLink>
      </template>

      <!-- Teachers with no assignments should go to Assignment Manager -->
      <template v-else-if="sidebarFilter === 'ASSIGNED'">
        <h2>{{ $t('You haven’t assigned anything...yet!') }}</h2>
        <p>
          {{ $t('Keep track of Studio creations you’ve assigned here.') }}
          <br>
          {{ $t('Manage your Assignments in Assignment Manager.') }}
        </p>
        <a
          class="home-empty-state__button"
          :href="`${domains.appDomain}/assignments`"
        >
          {{ $t('Go to Assignment Manager') }}
        </a>
      </template>

      <!-- Users with whom nothing has been shared have no CTA -->
      <template v-else-if="sidebarFilter === 'SHARED_WITH_ME'">
        <h2>{{ $t('Nothing’s been shared with you...yet!') }}</h2>
        <p>
          {{ $t('When someone shares a board or slideshow with you, it shows up here!') }}
        </p>
      </template>

      <!-- Trash view is empty -->
      <template v-else-if="sidebarFilter === 'TRASH'">
        <h2>{{ $t('Nothing’s in the Trash!') }}</h2>
        <p>
          {{ $t('Any item you delete will be placed here.') }}
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import {
  useUserStore,
} from '@/stores';

export default {
  name: 'HomeEmptyState',
  props: {
    sidebarFilter: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState(useUserStore, [
      'domains',
      'userIsStudent',
      'userHasActivityTemplates',
    ]),
  },
};
</script>

<style lang="stylus">
.home-empty-state {
  align-items: center;
  border: 1px solid $nebula-color-interface-blue-300;
  border-radius: $nebula-border-radius-button-default;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 0 auto;
  max-width: 1230px;
  padding: $nebula-space-4x;

  &__illustration-artistic, &__illustration-sniffing, &__illustration-keiki {
    flex-grow: 0;
    flex-shrink: 0;
  }

  &__illustration-artistic, &__illustration-keiki {
    margin-inline-end: $nebula-space-4x;
  }

  &__illustration-sniffing {
    margin-inline-end: $nebula-space-2x;
  }

  &__body {
    h2 {
      nebula-text-h3();
      margin: 0 0 $nebula-space-2x;
    }

    p {
      nebula-text-body-2();
      margin: 0 0 $nebula-space-2x;

      &:last-of-type {
        margin-bottom: $nebula-space-3x;
      }
    }
  }

  &__button {
    background-color: $nebula-color-interactive-blue-300;
    border: 0;
    border-radius: $nebula-border-radius-button-default;
    color: $nebula-color-white;
    display: inline-block;
    font-size: $nebula-font-size-body-1;
    line-height: 1.4;
    margin: 0;
    padding: $nebula-space-1x $nebula-space-2x;
    text-decoration: none;
    transition: $nebula-transition-default;

    &:hover,
    &:focus {
      background: $nebula-color-interactive-blue-400;
    }

    &:active {
      background: $nebula-color-interactive-blue-500;
    }
  }

  @media only screen and (min-width: $nebula-breakpoints-tablet-landscape) {
    flex-wrap: nowrap;
  }

  @media only screen and (max-width: $nebula-breakpoints-tablet-landscape - 1) {
    &__body {
      margin-left: $nebula-space-2x;
      margin-right: $nebula-space-2x;
    }

    &__illustration-artistic, &__illustration-keiki {
      margin-bottom: $nebula-space-2x;
      margin-inline-end: 0;
    }

    &__illustration-sniffing {
      margin-bottom: -($nebula-space-3x);
      margin-inline-end: 0;
      margin-top: -($nebula-space-3x);
    }
  }
}
</style>
